'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from 'react';
import { useDataLayerEvents } from './use-data-layer-events';
// Format timestamp
function formatTime(date) {
    return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
    });
}
// Get event type color
function getEventColor(eventName) {
    if (!eventName)
        return '#6b7280'; // gray
    if (eventName.startsWith('gtm.'))
        return '#8b5cf6'; // purple - GTM internal
    if (eventName === 'page_view' || eventName === 'pageview')
        return '#3b82f6'; // blue
    if (eventName.includes('click'))
        return '#10b981'; // green
    if (eventName.includes('conversion') || eventName.includes('purchase'))
        return '#f59e0b'; // amber
    if (eventName.includes('error'))
        return '#ef4444'; // red
    return '#06b6d4'; // cyan - custom events
}
function truncateValue(value) {
    const str = String(value);
    return str.length > 20 ? str.slice(0, 20) + '...' : str;
}
// Get a preview of the event data
function getEventPreview(data) {
    const keys = Object.keys(data).filter(k => k !== 'event' && !k.startsWith('gtm.'));
    if (keys.length === 0)
        return '';
    const preview = keys.slice(0, 3).map(k => `${k}: ${truncateValue(data[k])}`);
    return `{ ${preview.join(', ')}${keys.length > 3 ? ', ...' : ''} }`;
}
export function GTMEventsViewer({ maxEvents = 50 }) {
    const { events, isCapturing, toggleCapturing, clearEvents } = useDataLayerEvents(maxEvents);
    const [expandedEventId, setExpandedEventId] = useState(null);
    const [filter, setFilter] = useState('');
    const filteredEvents = useMemo(() => {
        if (!filter)
            return events;
        const lowerFilter = filter.toLowerCase();
        return events.filter(event => event.eventName?.toLowerCase().includes(lowerFilter) ||
            JSON.stringify(event.data).toLowerCase().includes(lowerFilter));
    }, [events, filter]);
    const handleToggleExpand = useCallback((eventId) => {
        setExpandedEventId(prev => (prev === eventId ? null : eventId));
    }, []);
    const handleFilterChange = useCallback((e) => {
        setFilter(e.target.value);
    }, []);
    // Styles
    const containerStyle = useMemo(() => ({ color: '#ffffff' }), []);
    const headerStyle = useMemo(() => ({ color: '#ffffff' }), []);
    const dimTextStyle = useMemo(() => ({ color: 'rgba(255, 255, 255, 0.6)' }), []);
    const inputStyle = useMemo(() => ({
        backgroundColor: '#0f0f0f',
        border: '1px solid #2a2a2a',
        color: '#ffffff',
    }), []);
    const buttonStyle = useMemo(() => ({
        backgroundColor: '#0f0f0f',
        border: '1px solid #2a2a2a',
        color: '#ffffff',
    }), []);
    const activeButtonStyle = useMemo(() => ({
        backgroundColor: '#166534',
        border: '1px solid #22c55e',
        color: '#ffffff',
    }), []);
    const pausedButtonStyle = useMemo(() => ({
        backgroundColor: '#991b1b',
        border: '1px solid #ef4444',
        color: '#ffffff',
    }), []);
    const eventListStyle = useMemo(() => ({
        backgroundColor: '#0f0f0f',
        border: '1px solid #2a2a2a',
    }), []);
    const eventItemStyle = useMemo(() => ({
        borderBottom: '1px solid #2a2a2a',
    }), []);
    const expandedDataStyle = useMemo(() => ({
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
    }), []);
    return (_jsxs("div", { style: containerStyle, children: [_jsxs("div", { className: 'flex items-center justify-between mb-3', children: [_jsx("div", { className: 'text-sm font-medium', style: headerStyle, children: "GTM / DataLayer Events" }), _jsxs("div", { className: 'flex items-center gap-2', children: [_jsxs("span", { className: 'text-xs', style: dimTextStyle, children: [filteredEvents.length, " events"] }), _jsx("button", { type: 'button', onClick: toggleCapturing, className: 'px-2 py-1 rounded text-xs font-medium transition-colors', style: isCapturing ? activeButtonStyle : pausedButtonStyle, children: isCapturing ? '● Capturing' : '○ Paused' }), _jsx("button", { type: 'button', onClick: clearEvents, className: 'px-2 py-1 rounded text-xs transition-colors hover:opacity-80', style: buttonStyle, children: "Clear" })] })] }), _jsx("div", { className: 'mb-3', children: _jsx("input", { type: 'text', placeholder: 'Filter events...', value: filter, onChange: handleFilterChange, className: 'w-full px-3 py-1.5 rounded text-sm', style: inputStyle }) }), _jsx("div", { className: 'rounded-lg overflow-hidden max-h-64 overflow-y-auto', style: eventListStyle, children: filteredEvents.length === 0 ? (_jsx("div", { className: 'p-4 text-center text-xs', style: dimTextStyle, children: events.length === 0
                        ? 'No dataLayer events captured yet. Events will appear here when GTM fires.'
                        : 'No events match your filter.' })) : (_jsx("div", { children: filteredEvents
                        .slice()
                        .reverse()
                        .map(event => (_jsxs("div", { style: eventItemStyle, children: [_jsxs("button", { type: 'button', onClick: () => handleToggleExpand(event.id), className: 'w-full flex items-center gap-3 p-2 text-left hover:bg-white/5 transition-colors', children: [_jsx("div", { className: 'w-2 h-2 rounded-full flex-shrink-0', style: { backgroundColor: getEventColor(event.eventName) } }), _jsx("span", { className: 'text-xs font-mono flex-shrink-0', style: dimTextStyle, children: formatTime(event.timestamp) }), _jsx("span", { className: 'text-sm font-medium truncate flex-1', style: { color: getEventColor(event.eventName) }, children: event.eventName || 'dataLayer.push' }), _jsx("span", { className: 'text-xs truncate max-w-[200px]', style: dimTextStyle, children: getEventPreview(event.data) }), _jsx("span", { className: 'text-xs', style: dimTextStyle, children: expandedEventId === event.id ? '▼' : '▶' })] }), expandedEventId === event.id && (_jsx("div", { className: 'p-3 m-2 rounded text-xs font-mono overflow-x-auto', style: expandedDataStyle, children: _jsx("pre", { style: { color: '#a5f3fc' }, children: JSON.stringify(event.data, null, 2) }) }))] }, event.id))) })) }), _jsxs("div", { className: 'mt-3 flex items-center gap-4 text-xs', style: dimTextStyle, children: [_jsxs("div", { className: 'flex items-center gap-1', children: [_jsx("div", { className: 'w-2 h-2 rounded-full', style: { backgroundColor: '#8b5cf6' } }), _jsx("span", { children: "GTM Internal" })] }), _jsxs("div", { className: 'flex items-center gap-1', children: [_jsx("div", { className: 'w-2 h-2 rounded-full', style: { backgroundColor: '#3b82f6' } }), _jsx("span", { children: "Page View" })] }), _jsxs("div", { className: 'flex items-center gap-1', children: [_jsx("div", { className: 'w-2 h-2 rounded-full', style: { backgroundColor: '#06b6d4' } }), _jsx("span", { children: "Custom Event" })] }), _jsxs("div", { className: 'flex items-center gap-1', children: [_jsx("div", { className: 'w-2 h-2 rounded-full', style: { backgroundColor: '#f59e0b' } }), _jsx("span", { children: "Conversion" })] })] })] }));
}
